/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { useRouter } from 'next/router';

import { requestLogout } from '@src/utils/requestLogout';
import useTranslation from '@src/hooks/useTranslation';
import { SwitchSystemIcon } from '@src/components/common/icons/SwitchSystemIcon';
import { HRLogoIcon } from '@src/components/common/icons/HRLogoIcon';
import { SwitchHRLogoIconWhite } from '@src/components/common/icons/SwitchHRLogoIconWhite';
import { SwitchCourseLogoIconGreen } from '@src/components/common/icons/SwitchCourseLogoIconGreen';
import { SelectedSystemIcon } from '@src/components/common/icons/SelectedSystemIcon';
import { getUserProfile } from '@src/store/selectors/user';
import { getThemeSelector } from '@src/store/selectors';
import { logoutCall } from '@src/store/actions/session';
import { isStaging, isDev } from '@src/utils/env';

import { RowWrapper } from './hr/punchIn/styles';

const SystemContent = styled.div`
	display: flex;
	position: relative;
	.text {
		color: #676767;
		font-size: 12px;
		font-weight: 400;
	}
	:hover {
		background: #F5F5F7;
		border-radius: 100px;
		.switch-system-option {
			display: grid;
		}
		.switch-system-icon {
			path {
				fill: #3091FD;
				stroke: #3091FD;
			}
		}
	}
`;

const SystemOptionWrapper = styled(RowWrapper)`
	padding: 8px 0px;
	gap: 8px;
	justify-content: flex-start;
	cursor: pointer;
	:hover {
		border-radius: 8px;
		background: #F5F5F7;
	}
`;

const StyledSelectedSystemIcon = styled(SelectedSystemIcon)`
`;

export const SystemContentInfo = styled.div`
	position: absolute;
	background: transparent;
	padding-top: 60px;
	left: -5px;
	z-index: 9999999;
	display: none;
`;

const SystemOption = styled.div`
	padding: 20px;
	border-radius: 8px;
	background: #FFF;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.20);
	white-space: nowrap;
	display: grid;
	gap: 12px;
	padding: 12px 40px;
`;

const SystemText = styled.div`
	cursor: pointer;
  margin-right: 8px;
`;

const BleakText = styled.div`
	font-size: 14px;
	cursor: pointer;
	border-radius: 8px;
	padding: 8px;
	:hover {
		background: #F5F5F7;
	}
`;

const AvatarContent = styled.div`
	margin-left: 12px;
	position: relative;
	:hover {
		.avatar-info {
			display: block;
		}
	}
`;

const AvatarIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 32px;
	border: 1px solid #FFF;
	background: #DEDEDE;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	color: white;
	background: #1150ae;
	cursor: pointer;
`;
const LargeAvatarIcon = styled(AvatarIcon)`
	width: 46px;
	height: 46px;
	border-radius: 46px;
	cursor: initial;
`;

const AvatarImg = styled.img`
	width: 32px;
	height: 32px;
	border-radius: 32px;
`;
const LargeAvatarImg = styled(AvatarImg)`
	width: 46px;
	height: 46px;
	border-radius: 46px;
	cursor: initial;
`;

const InfoContent = styled.div`
	position: absolute;
	background: transparent;
	padding-top: 20px;
	right: 0;
	z-index: 999;
	display: none;
	right: -10px;
`;

const AvatarInfo = styled.div`
	padding: 12px;
	border-radius: 8px;
	background: #FFF;
	width: 240px;
	box-shadow: 0px 0px 12px 0px rgba(69, 65, 78, 0.09);
`;

const Divider = styled.div`
	width: 100%;
	height: 1px;
	background: #E9E9E9;
	margin-bottom: 12px;
`;

const ProfileButton = styled.div`
	padding: 8px;
	font-size: 14px;
	margin-bottom: 4px;
	cursor: pointer;
	:hover {
		border-radius: 8px;
		background: #F5F5F7;
	}
`;

const LogOutButton = styled.div<{ disabled?: boolean }>`
	padding: 8px;
	font-size: 14px;
	color:  ${props => (props.disabled ? '#DDD' : '#222')};
	cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
	pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
	:hover {
		border-radius: 8px;
		background: #F5F5F7;
	}
`;

const SystemNameWrapper = styled.div`
	color: #222;
	font-size: 18px;
	font-weight: 400;
	height: 27px;
	display: flex;
	margin-left: 12px;
	align-items: center;
	& span {
		margin-left: 4px;
	}
`;

const UserInfoBlock = styled.div`
	max-width: 162px;
  display: flex;
  flex-direction: column;
	justify-content: center;
  gap: 4px;
`;

const EllipsisText = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Name = styled(EllipsisText)`
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  color: #222;
`;

const Email = styled(EllipsisText)`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #676767;
`;

const SideMenuHeader = (): JSX.Element => {
	const { t } = useTranslation();
	const router = useRouter();
	const userProfile = useSelector(getUserProfile) as any;
	const { themes } = useSelector(getThemeSelector) as any;
	const isStaleData = useSelector((state: RootStateOrAny) => state.punchIn.isPunchInStale) || false;
	const dispatch = useDispatch();
	const handleLogout = () => {
		if (isStaleData) return;
		requestLogout(() => dispatch(logoutCall()));
	};
	return (
		<>
			<RowWrapper>
				<SystemContent>
					<SwitchSystemIcon className="switch-system-icon" />
					{
						themes?.can_switch_course_system || themes?.can_switch_hr_system ? (
							<SystemContentInfo className="switch-system-option">
								<SystemOption className="switch-system-option">
									<div className="text">{t('hr:common:action:switch_system')}</div>
									{
										themes?.can_switch_hr_system && (
											<SystemOptionWrapper onClick={() => router.push('/hr')}>
												<SwitchHRLogoIconWhite />
												<SystemText>
													{t('common:brand:hr_system_name')}
												</SystemText>
												<StyledSelectedSystemIcon />
											</SystemOptionWrapper>
										)
									}
									{
										themes?.can_switch_course_system && (
											<SystemOptionWrapper onClick={() => router.push('/training')}>
												<SwitchCourseLogoIconGreen />
												<SystemText>
													{t('common:brand:training_system_name')}
												</SystemText>
											</SystemOptionWrapper>
										)
									}
								</SystemOption>
							</SystemContentInfo>
						) : null
					}
				</SystemContent>
				<SystemNameWrapper onClick={() => router.push('/hr')}>
					<HRLogoIcon />
					<span>{t('common:brand:hr_system_name')}</span>
				</SystemNameWrapper>
			</RowWrapper>
			<RowWrapper>
				<BleakText
					onClick={() => {
						if (isDev) {
							window.open('https://dev.teaches.cc/admin/login', '_blank');
						} else if (isStaging) {
							window.open('https://staging.teaches.cc/admin/login', '_blank');
						} else {
							window.open('https://teaches.cc/admin/login', '_blank');
						}
					}}
				>
					{t('hr:menu:go_to_admin_console')}
				</BleakText>
				<AvatarContent>
					{
						userProfile?.profile_picture_link?.link ? (
							<AvatarImg src={userProfile.profile_picture_link.link} />
						) : (
							<AvatarIcon>
								{userProfile?.name?.[0]}
							</AvatarIcon>
						)
					}
					<InfoContent className="avatar-info">
						<AvatarInfo>
							<div style={{ padding: '4px 0 16px', display: 'flex', gap: '8px' }}>
								{
									userProfile?.profile_picture_link?.link ? (
										<LargeAvatarImg src={userProfile.profile_picture_link.link} />
									) : (
										<LargeAvatarIcon>
											{userProfile?.name?.[0]}
										</LargeAvatarIcon>
									)
								}
								<UserInfoBlock>
									<Name>{userProfile?.name}</Name>
									<Email>{userProfile?.email}</Email>
								</UserInfoBlock>
							</div>
							<Divider />
							<ProfileButton onClick={() => router.push('/hr/personal-profile')}>
								{t('hr:menu:profile')}
							</ProfileButton>
							<LogOutButton disabled={isStaleData} onClick={handleLogout}>
								{t('common:action:logout')}
							</LogOutButton>
						</AvatarInfo>
					</InfoContent>
				</AvatarContent>
			</RowWrapper>
		</>
	);
};

export default SideMenuHeader;
